import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { addDays, addMonths, format } from "date-fns";
import { fr } from "date-fns/locale";
import { CalendarDays, ChevronLeft, ChevronRight, Plus, Users, X } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import CalendarMonth from "@/components/CalendarMonth";
import NewPublicationModal from "@/components/NewPublicationModal";
import StatusBadge from "@/components/StatusBadge";
import { STATUSES, formatLabel, platformLabel, frDate } from "@/lib/constants";
import { toast } from "sonner";

const fieldCls = "text-xs border border-[#E2E8F0] rounded-lg px-2.5 py-1.5 bg-white text-neutral-700 hover:border-neutral-300 transition-colors min-w-[150px]";

export default function GlobalCalendar({ embedded = false, clientId = "" }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pubs, setPubs] = useState([]);
  const [clients, setClients] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [month, setMonth] = useState(new Date());
  const [view, setView] = useState("month");
  const [filters, setFilters] = useState({ client: clientId, status: "", collab: "" });
  const [modal, setModal] = useState({ open: false, date: null });
  // Sélection client pilotée par la page parente (ex. onglet CM) — force le filtre.
  useEffect(() => { setFilters((f) => ({ ...f, client: clientId })); }, [clientId]);

  const load = () => api.get("/publications").then((r) => setPubs(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/users").then((r) => setCollaborators(r.data || [])).catch(() => {});
    // Miniatures à jour : rechargement dès qu'une publication est modifiée ailleurs
    const h = () => load();
    window.addEventListener("sf:pubs", h);
    return () => window.removeEventListener("sf:pubs", h);
  }, []);

  const colorMap = useMemo(() => {
    const m = {};
    clients.forEach((c) => (m[c.id] = c.color));
    return m;
  }, [clients]);

  const filtered = useMemo(() => pubs.filter((p) =>
    (!filters.client || p.client_id === filters.client) &&
    (!filters.status || p.status === filters.status) &&
    (!filters.collab || p.assigned_to === filters.collab)
  ).map((p) => ({ ...p, client_name: clients.find((c) => c.id === p.client_id)?.name })), [pubs, filters, clients]);

  const onMove = async (id, date) => {
    setPubs((ps) => ps.map((p) => (p.id === id ? { ...p, date } : p)));
    try {
      await api.patch(`/publications/${id}`, { date });
      toast.success("Publication déplacée");
    } catch { toast.error("Erreur"); load(); }
  };

  const navStep = (dir) => setMonth((m) => (view === "week" ? addDays(m, dir * 7) : addMonths(m, dir)));
  const periodLabel = format(month, "MMMM yyyy", { locale: fr });
  const selectedClient = clients.find((c) => c.id === filters.client);

  const sortedList = useMemo(() => [...filtered].sort((a, b) => (a.date + (a.time || "")).localeCompare(b.date + (b.time || ""))), [filtered]);

  return (
    <div className="space-y-4" data-testid="global-calendar">
      {/* En-tête allégé (masqué en mode embarqué, ex. onglet CM) */}
      {!embedded && (
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#0F172A]">Calendrier éditorial</h1>
          <p className="text-sm font-semibold text-[#0F172A] mt-0.5">Community Management</p>
          <p className="text-xs text-neutral-400 mt-0.5">Planifiez et suivez vos publications sur les réseaux sociaux.</p>
        </div>
        <p className="text-[#64748B] italic text-sm leading-snug text-right hidden md:block" data-testid="calendar-quote">« Des contenus utiles,<br />pour des marques qui comptent. »</p>
      </div>
      )}

      {/* Barre de filtres unifiée */}
      <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm px-3 py-2.5 flex flex-wrap items-center gap-x-3 gap-y-2" data-testid="calendar-filterbar">
        <div className="flex items-center gap-1">
          <button data-testid="cal-prev" onClick={() => navStep(-1)}
            className="p-1.5 rounded-lg hover:bg-neutral-100 text-neutral-500 transition-colors"><ChevronLeft className="w-4 h-4" /></button>
          <span className="flex items-center gap-1.5 text-sm font-semibold text-[#0F172A] capitalize px-1" data-testid="calendar-month-label">
            <CalendarDays className="w-4 h-4 text-neutral-400" />
            {view === "week" ? `Semaine du ${format(startOfWeekDate(month), "d MMM", { locale: fr })}` : periodLabel}
          </span>
          <button data-testid="cal-next" onClick={() => navStep(1)}
            className="p-1.5 rounded-lg hover:bg-neutral-100 text-neutral-500 transition-colors"><ChevronRight className="w-4 h-4" /></button>
        </div>

        <div className="flex rounded-full border border-[#E2E8F0] bg-neutral-50 p-0.5" data-testid="view-toggle">
          {[["month", "Mois"], ["week", "Semaine"], ["list", "Liste"]].map(([v, l]) => (
            <button key={v} data-testid={`view-${v}`} onClick={() => setView(v)}
              className={`text-xs font-medium rounded-full px-3.5 py-1.5 transition-colors ${view === v ? "bg-[#0B132B] text-white" : "text-neutral-500 hover:text-neutral-900"}`}>
              {l}
            </button>
          ))}
        </div>

        <span className="w-px h-7 bg-neutral-200 hidden lg:block" />

        {!clientId && (
        <label className="flex flex-col gap-0.5">
          <span className="text-[10px] font-semibold text-neutral-400">Client</span>
          <select data-testid="filter-client" value={filters.client} onChange={(e) => setFilters({ ...filters, client: e.target.value })} className={fieldCls}>
            <option value="">Tous les clients</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
        )}
        <label className="flex flex-col gap-0.5">
          <span className="text-[10px] font-semibold text-neutral-400">Statut</span>
          <select data-testid="filter-status" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className={fieldCls}>
            <option value="">Tous les statuts</option>
            {STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-0.5">
          <span className="text-[10px] font-semibold text-neutral-400">Collaborateur</span>
          <select data-testid="filter-collab" value={filters.collab} onChange={(e) => setFilters({ ...filters, collab: e.target.value })} className={fieldCls}>
            <option value="">Tous les collaborateurs</option>
            {collaborators.length === 0 && user && <option value={user.email}>{user.name}</option>}
            {collaborators.map((u) => <option key={u.id || u.email} value={u.email}>{u.name}</option>)}
          </select>
        </label>

        <button data-testid="new-pub-btn" onClick={() => setModal({ open: true, date: null })}
          className="ml-auto inline-flex items-center gap-1.5 bg-[#0B132B] text-white rounded-xl px-4 py-2 text-xs font-semibold hover:bg-[#1C2A4F] transition-colors">
          <Plus className="w-3.5 h-3.5" /> Nouvelle publication
        </button>
      </div>

      {/* Bandeau filtre client actif */}
      {selectedClient && (
        <div className="flex items-center gap-2 bg-[#E0F2FE]/60 border border-[#BAE6FD] rounded-xl px-4 py-2.5" data-testid="client-filter-banner">
          <Users className="w-4 h-4 text-[#0369A1] shrink-0" />
          <p className="text-xs text-[#0F172A]">
            Vous visualisez uniquement les publications du client <strong>{selectedClient.name}</strong>.
            <button onClick={() => setFilters({ ...filters, client: "" })} className="ml-2 font-semibold text-[#0369A1] hover:underline" data-testid="client-filter-clear">
              Afficher tous les clients
            </button>
          </p>
          <Link to={`/clients/${selectedClient.id}`} data-testid="client-filter-fiche"
            className="inline-flex items-center gap-1 text-[11px] font-semibold bg-[#0B132B] text-white rounded-lg px-3 py-1.5 hover:bg-[#1C2A4F] transition-colors shrink-0">
            Accéder à la fiche client
          </Link>
          <button onClick={() => setFilters({ ...filters, client: "" })} className="ml-auto text-neutral-400 hover:text-neutral-700" data-testid="client-filter-close">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {view === "list" ? (
        <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm overflow-hidden" data-testid="calendar-list">
          <div className="grid grid-cols-[90px_1fr_140px_110px_120px] gap-3 px-5 py-3 border-b border-neutral-100 text-[10px] font-semibold uppercase tracking-wider text-neutral-400">
            <span>Date</span><span>Publication</span><span>Client</span><span>Format</span><span className="text-right">Statut</span>
          </div>
          {sortedList.length === 0 && <p className="px-5 py-8 text-sm text-neutral-400">Aucune publication pour ces filtres.</p>}
          {sortedList.map((p) => (
            <button key={p.id} data-testid={`list-pub-${p.id}`} onClick={() => navigate(`/publications/${p.id}`)}
              className="w-full grid grid-cols-[90px_1fr_140px_110px_120px] gap-3 items-center px-5 py-2.5 border-b border-neutral-100 last:border-0 hover:bg-neutral-50 transition-colors text-left">
              <span className="text-xs text-neutral-500">{frDate(p.date)}<br /><span className="text-neutral-400">{p.time}</span></span>
              <span className="text-[13px] font-medium text-[#0F172A] truncate flex items-center gap-2">
                <span className="w-2 h-2 rounded-full shrink-0" style={{ background: colorMap[p.client_id] || "#94A3B8" }} />
                {p.title}
              </span>
              <span className="text-xs text-neutral-500 truncate">{p.client_name}</span>
              <span className="text-xs text-neutral-500">{platformLabel(p.platform)} · {formatLabel(p.format)}</span>
              <span className="text-right"><StatusBadge status={p.status} /></span>
            </button>
          ))}
        </div>
      ) : (
        <CalendarMonth
          pubs={filtered}
          colorOf={(p) => colorMap[p.client_id] || "#94A3B8"}
          month={month}
          setMonth={setMonth}
          view={view}
          thumbnails
          hideHeader
          onPubClick={(p) => navigate(`/publications/${p.id}`)}
          onDayClick={(date) => setModal({ open: true, date })}
          onMove={onMove}
        />
      )}
      <NewPublicationModal
        open={modal.open} setOpen={(v) => setModal({ ...modal, open: v })}
        clients={clients} defaultDate={modal.date} defaultClientId={clientId || undefined}
        onCreated={load}
      />
    </div>
  );
}

function startOfWeekDate(d) {
  const date = new Date(d);
  const day = (date.getDay() + 6) % 7;
  date.setDate(date.getDate() - day);
  return date;
}
